import React, { useEffect, useState } from "react";
import {Check,CheckCheck,ChevronDown,FileText,Info,Loader2,Send,Sparkles,X} from "lucide-react";
import templateService from "src/services/templateService";


const getTemplateVariables = (template) => {
    const body = template?.components?.find((component) => component.type === "BODY");
    if (!body?.text) {
        return [];
    }

    const matches = body.text.match(/\{\{\d+\}\}/g) || [];

    return [...new Set(matches)].map((variable) => {
            const key = variable.replace(/[{}]/g, "",);

            return {
                key,
                variable,
                label: `Variable ${key}`,
                placeholder: `Enter value for ${variable}`,
            };
        },
    );
};


const replaceVariables = (text,values) => {
    if (!text) return "";
    
    return text.replace(/\{\{(\d+)\}\}/g ,(_, number) => values[number] || `{{${number}}}`,);
};


function TemplateSend({conversation,onMessageSent,onSendTemplate,onClose}) {
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [templatesError, setTemplatesError] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showVariableModal, setShowVariableModal] = useState(false);
    const [sending, setSending] = useState(false);
    const selectedVariables = getTemplateVariables(selectedTemplate);

    useEffect(() => {
        const phoneNumberId =
            conversation?.phone_number_id ||
            localStorage.getItem("phoneNumberId");

        if (!phoneNumberId) {
            setLoadingTemplates(false);
            setTemplatesError("No WhatsApp number is selected.");
            return;
        }

        let active = true;
        setLoadingTemplates(true);

        templateService
            .forPhoneNumber(phoneNumberId, { status: "APPROVED" })
            .then((res) => {
                if (!active) return;
                setTemplates(res?.data?.data ?? []);
                setTemplatesError("");
            })
            .catch((err) => {
                if (!active) return;
                setTemplatesError(
                    err?.response?.data?.message || "Unable to load templates.",
                );
            })
            .finally(() => active && setLoadingTemplates(false));

        return () => {
            active = false;
        };
    }, [conversation?.id, conversation?.phone_number_id]);

    const handleTemplateChange = (event) => {
        const templateId = event.target.value;

        if (!templateId) {
            setSelectedTemplate(null);
            return;
        }

        const template = templates.find((item) => String(item.id || item.name) === String(templateId));

        if (!template) {
            return;
        }

        setSelectedTemplate(template);
    };

   
    const handleOpenVariableModal = () => {
            if (!selectedTemplate || sending) {
                return;
            }

            const variables = getTemplateVariables(selectedTemplate);

            if (variables.length === 0) {
                handleSendTemplate({});
                return;
            }

            setShowVariableModal(true);
        };
        
    const handleSendTemplate = async (values = {}) => {
        if (!selectedTemplate || sending || !onSendTemplate) {
            return false;
        }

        try {
            setSending(true);
            const success = await onSendTemplate(selectedTemplate,values);
            if (success) {
                setShowVariableModal(false);
                setSelectedTemplate(null);

                await onMessageSent?.();
                onClose?.();
            }

            return success;
        } catch (error) {
            console.error("Failed to send template:",error);
            return false;
        } finally {
            setSending(false);
        }
    };
    
    const selectedBody = selectedTemplate?.components?.find((component) => component.type === "BODY");

  return (
  <>
    <div className="relative overflow-hidden py-3">
      <div className="relative mx-auto max-w-4xl">
        <div className="overflow-hidden rounded-2xl border border-border-light bg-card-bg shadow-[0_4px_20px_rgba(20,125,181,0.06)] transition-colors duration-200 dark:border-dark-border dark:bg-dark-card">

          {/* Header */}
          <div className="flex items-center justify-between border-b border-border-light px-4 py-3 dark:border-dark-border">
            <div className="flex min-w-0 items-center gap-3">

              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-theme-50 text-primary shadow-sm dark:bg-dark-soft-blue dark:text-dark-primary">
                <FileText size={16} strokeWidth={1.9} />

                <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-card-bg shadow-[0_2px_6px_rgba(20,125,181,0.12)] dark:bg-dark-card">
                  <Sparkles
                    size={9}
                    strokeWidth={2}
                    className="text-secondary dark:text-dark-primary"
                  />
                </span>
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate text-[12px] font-semibold text-text-primary dark:text-dark-text-primary">
                    Send a template
                  </p>

                  <span className="rounded-full bg-theme-50 px-2 py-0.5 text-[9px] font-semibold text-primary dark:bg-dark-soft-blue dark:text-dark-primary">
                    WhatsApp
                  </span>
                </div>

                <p className="mt-0.5 text-[10px] text-text-muted dark:text-dark-text-muted">
                  Continue the conversation with an approved template
                </p>
              </div>
            </div>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="ml-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-text-muted transition-all duration-200 hover:bg-theme-50 hover:text-primary active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 dark:text-dark-text-muted dark:hover:bg-dark-soft-blue dark:hover:text-dark-primary"
              title="Close template"
            >
              <X size={17} strokeWidth={2} />
            </button>
          </div>

          {/* Body */}
          <div className="p-3">
            <div className="flex gap-2">

              {/* Template Select */}
              <div className="relative min-w-0 flex-1">
                <select
                  value={
                    selectedTemplate?.id ||
                    selectedTemplate?.name ||
                    ""
                  }
                  onChange={handleTemplateChange}
                  disabled={sending || loadingTemplates || templates.length === 0}
                  className="h-11 w-full cursor-pointer appearance-none rounded-xl border border-border-color bg-app-bg px-3.5 pr-10 text-[11px] font-medium text-text-secondary outline-none transition-all hover:border-theme-200 hover:bg-card-bg focus:border-primary focus:bg-card-bg focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-text-secondary dark:hover:border-dark-primary dark:hover:bg-dark-card-bg dark:focus:border-dark-primary dark:focus:bg-dark-card-bg dark:focus:ring-dark-primary/10"
                >
                  <option value="">
                    {loadingTemplates
                      ? "Loading templates…"
                      : templatesError
                        ? templatesError
                        : templates.length === 0
                          ? "No approved templates — sync from the Templates page"
                          : "Choose a template..."}
                  </option>

                  {templates.map((template) => (
                    <option
                      key={template.id || template.name}
                      value={template.id || template.name}
                    >
                      {template.name}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={15}
                  strokeWidth={1.8}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-muted dark:text-dark-text-muted"
                />
              </div>

              {/* Send / Continue */}
              <button
                type="button"
                onClick={handleOpenVariableModal}
                disabled={!selectedTemplate || sending}
                className="group flex h-11 shrink-0 items-center gap-2 rounded-xl bg-primary px-4 text-[11px] font-semibold text-white shadow-[0_5px_14px_rgba(20,125,181,0.20)] transition-all duration-200 hover:-translate-y-[1px] hover:bg-primary-dark hover:shadow-[0_7px_18px_rgba(20,125,181,0.25)] disabled:cursor-not-allowed disabled:bg-border-color disabled:text-text-muted disabled:shadow-none dark:bg-dark-primary dark:hover:bg-dark-primary/90"
              >
                {sending ? (
                  <Loader2
                    size={14}
                    className="animate-spin"
                  />
                ) : (
                  <Send
                    size={14}
                    strokeWidth={2}
                  />
                )}

                <span>
                  {selectedVariables.length > 0
                    ? "Continue"
                    : "Send"}
                </span>
              </button>
            </div>

            {/* Selected Template */}
            {selectedTemplate && (
              <div className="mt-2.5 flex items-start gap-2.5 rounded-xl border border-theme-100 bg-theme-50/60 px-3 py-2.5 dark:border-dark-border dark:bg-dark-soft-blue">

                <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-theme-100 text-primary dark:bg-dark-card dark:text-dark-primary">
                  <Check
                    size={12}
                    strokeWidth={2.5}
                  />
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2">

                    <p className="text-[10px] font-semibold text-text-primary dark:text-dark-text-primary">
                      {selectedTemplate.name}
                    </p>

                    <span className="rounded-md bg-theme-100 px-1.5 py-0.5 text-[8px] font-semibold uppercase text-primary dark:bg-dark-card dark:text-dark-primary">
                      {selectedTemplate.category}
                    </span>
                  </div>

                  <p className="mt-0.5 line-clamp-2 text-[9px] leading-4 text-text-muted dark:text-dark-text-muted">
                    {selectedBody?.text}
                  </p>
                </div>
              </div>
            )}

            {/* Info */}
            <div className="mt-2 flex items-center gap-1.5 px-1">
              <Info
                size={10}
                strokeWidth={1.8}
                className="shrink-0 text-text-muted dark:text-dark-text-muted"
              />

              <p className="text-[9px] leading-4 text-text-muted dark:text-dark-text-muted">
                Approved templates are required when the customer
                service window has expired.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Variable Modal */}
    {showVariableModal && selectedTemplate && (
      <TemplateVariableModal
        template={selectedTemplate}
        variables={selectedVariables}
        onClose={() => setShowVariableModal(false)}
        onSend={handleSendTemplate}
        sending={sending}
      />
    )}
  </>
);
}


function TemplateVariableModal({ template, variables, onClose, onSend, sending,}) {
    const [values, setValues] = useState({});
    const allFilled = variables.every((variable) => String( values[variable.key] || "").trim().length > 0);

    const handleChange = (key, value) => {
        setValues((previous) => ({
            ...previous,
            [key]: value,
        }));
    };

    const handleSubmit = async () => {
        if (!allFilled || sending) {
            return;
        }
        await onSend(values);
    };

   return (
  <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-900/20 p-0 backdrop-blur-[4px] sm:items-center sm:p-4 dark:bg-black/50">
    <div className="relative flex max-h-[95dvh] w-full flex-col overflow-hidden rounded-t-3xl border border-border-light bg-card-bg shadow-[0_30px_100px_rgba(20,90,125,0.18)] sm:max-h-[90vh] sm:max-w-[520px] sm:rounded-3xl dark:border-dark-border dark:bg-dark-card dark:shadow-[0_30px_100px_rgba(0,0,0,0.35)]">

      {/* =====================================================
          HEADER
      ====================================================== */}
      <div className="flex shrink-0 items-center justify-between border-b border-border-light px-4 py-3.5 sm:px-5 sm:py-4 dark:border-dark-border">
        <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-theme-50 text-primary dark:bg-dark-soft-blue dark:text-dark-primary">
            <Sparkles size={16} strokeWidth={1.9}/>
          </div>

          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold text-text-primary dark:text-dark-text-primary">
              Personalize template
            </p>

            <p className="mt-0.5 truncate text-[10px] text-text-muted dark:text-dark-text-muted">
              Enter the values for your message
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          disabled={sending}
          className="ml-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-text-muted transition-all duration-200 hover:bg-theme-50 hover:text-primary disabled:opacity-50 dark:text-dark-text-muted dark:hover:bg-dark-soft-blue dark:hover:text-dark-primary"
        >
          <X size={16} />
        </button>
      </div>

      {/* =====================================================
          CONTENT
      ====================================================== */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">

        {/* Template Info */}
        <div className="mb-4 flex min-w-0 items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-text-muted dark:text-dark-text-muted">
              Template
            </p>

            <p className="mt-1 truncate text-[12px] font-semibold text-text-primary dark:text-dark-text-primary">
              {template.name}
            </p>
          </div>

          <span className="shrink-0 rounded-full border border-theme-200 bg-theme-50 px-2.5 py-1 text-[9px] font-semibold text-primary dark:border-dark-primary dark:bg-dark-soft-blue dark:text-dark-primary">
            {template.category}
          </span>
        </div>

        {/* =====================================================
            MESSAGE PREVIEW
        ====================================================== */}
        <div className="mb-5">
          <p className="mb-2 text-[9px] font-semibold uppercase tracking-[0.08em] text-text-muted dark:text-dark-text-muted">
            Message preview
          </p>

          <div className="rounded-2xl border border-theme-100 bg-theme-50/60 p-2.5 sm:p-4 dark:border-dark-border dark:bg-dark-soft-blue/60">
            <div className="rounded-2xl rounded-tl-sm border border-border-light bg-card-bg px-3.5 py-3 shadow-[0_2px_8px_rgba(15,23,42,0.04)] sm:px-4 dark:border-dark-border dark:bg-dark-card dark:shadow-[0_2px_8px_rgba(0,0,0,0.18)]">

              {template.components?.map(
                (component, index) => {
                  if (component.type === "HEADER") {
                    return (
                      <p
                        key={index}
                        className="mb-1.5 break-words text-[11px] font-bold text-text-primary dark:text-dark-text-primary"
                      >
                        {component.text}
                      </p>
                    );
                  }

                  if (component.type === "BODY") {
                    return (
                      <p key={index} className="whitespace-pre-wrap break-words text-[11px] leading-5 text-text-secondary dark:text-dark-text-secondary">
                        {replaceVariables(component.text, values)}
                      </p>
                    );
                  }

                  if (component.type === "FOOTER") {
                    return (
                      <p key={index} className="mt-2 break-words text-[9px] text-text-muted dark:text-dark-text-muted">
                        {component.text}
                      </p>
                    );
                  }

                  return null;
                })}

              <div className="mt-2 flex items-center justify-end gap-1 text-[8px] text-text-muted dark:text-dark-text-muted">
                <span>Just now</span>
                <CheckCheck size={11} className="text-primary dark:text-dark-primary"/>
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            VARIABLES
        ====================================================== */}
        <div>
          <div className="mb-2.5 flex items-center justify-between gap-2">
            <p className="text-[10px] font-semibold text-text-primary dark:text-dark-text-primary">
              Template variables
            </p>

            <span className="shrink-0 text-[9px] text-text-muted dark:text-dark-text-muted">
              {variables.length} required
            </span>
          </div>

          <div className="space-y-3">
            {variables.map((variable, index) => (
                <div key={variable.key} className="min-w-0">
                  <label className="mb-1.5 flex min-w-0 items-center gap-1.5 sm:gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-theme-200 bg-theme-50 text-[9px] font-bold text-primary dark:border-dark-primary dark:bg-dark-soft-blue dark:text-dark-primary">
                      {index + 1}
                    </span>

                    <span className="min-w-0 truncate text-[10px] font-medium text-text-secondary dark:text-dark-text-secondary">
                      {variable.label}
                    </span>

                    <span className="shrink-0 text-[9px] text-text-muted dark:text-dark-text-muted">
                      {variable.variable}
                    </span>
                  </label>

                  <input
                    type="text"
                    value={values[variable.key] || ""}
                    onChange={(event) => handleChange(variable.key,event.target.value)}
                    placeholder={variable.placeholder}
                    disabled={sending}
                    className="h-10 w-full rounded-xl border border-border-light bg-app-bg px-3 text-[11px] text-text-primary outline-none placeholder:text-text-muted transition-all duration-200 hover:border-theme-200 focus:border-primary focus:bg-card-bg focus:ring-4 focus:ring-primary/[0.06] dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-text-primary dark:placeholder:text-dark-text-muted dark:hover:border-dark-primary dark:focus:border-dark-primary dark:focus:bg-dark-card-bg dark:focus:ring-dark-primary/[0.08]"
                  />
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* =====================================================
          FOOTER
      ====================================================== */}
      <div className="shrink-0 border-t border-border-light bg-app-bg px-4 py-3 sm:px-5 dark:border-dark-border dark:bg-dark-soft-blue">
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
          <p className="hidden text-[9px] text-text-muted sm:block dark:text-dark-text-muted">
            All fields are required
          </p>

          <div className="flex w-full items-center gap-2 sm:w-auto">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="h-10 flex-1 rounded-xl px-3.5 text-[10px] font-semibold text-text-secondary transition-all duration-200 hover:bg-theme-50 hover:text-primary sm:h-9 sm:flex-none dark:text-dark-text-secondary dark:hover:bg-dark-card-bg dark:hover:text-dark-primary"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allFilled || sending}
              className="flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-[10px] font-semibold text-white shadow-[0_4px_12px_rgba(20,125,181,0.16)] transition-all duration-200 hover:bg-primary-dark hover:shadow-[0_5px_16px_rgba(20,125,181,0.22)] active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-theme-100 disabled:text-text-muted disabled:shadow-none sm:h-9 sm:flex-none dark:bg-dark-primary dark:text-white dark:hover:bg-dark-primary dark:disabled:bg-dark-border dark:disabled:text-dark-text-muted"
            >
              {sending ? (
                <Loader2 size={13} className="animate-spin"/>
              ) : (
                <Send size={13} strokeWidth={2}/>
              )}

              {sending ? "Sending..." : "Send template"}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);
}

export default TemplateSend;